import { useCurrentStateAndParams, useRouter } from '@uirouter/react';

import {
  UserAccessViewModel,
  TeamAccessViewModel,
} from '@/portainer/models/access';
import { notifyError, notifySuccess } from '@/portainer/services/notifications';
import { PortainerNamespaceAccessesConfigMap } from '@/react/kubernetes/configs/constants';
import { useEnvironmentId } from '@/react/hooks/useEnvironmentId';
import { useConfigMap } from '@/react/kubernetes/configs/queries/useConfigMap';
import { useUpdateK8sConfigMapMutation } from '@/react/kubernetes/configs/queries/useUpdateK8sConfigMapMutation';
import { AccessDatatable as NamespaceAccessDatatable } from '@/react/portainer/access-control/AccessManagement/AccessDatatable/AccessDatatable';

import { useNamespaceAccesses } from '../useNamespaceAccesses';
import { useUpdateNamespaceAccessesMutation } from '../useUpdateNamespaceAccessesMutation';
import { createAccessesPayload } from '../createAccessesPayload';
import { createUnauthorizeAccessConfigMapPayload } from '../createAccessConfigMapPayload';
import { NamespaceAccess } from '../types';

const tableKey = 'kubernetes_resourcepool_access';

export function AccessDatatable() {
  const {
    params: { id: namespaceName },
  } = useCurrentStateAndParams();
  const router = useRouter();
  const environmentId = useEnvironmentId();
  const accessConfigMapQuery = useConfigMap(
    environmentId,
    PortainerNamespaceAccessesConfigMap.namespace,
    PortainerNamespaceAccessesConfigMap.configMapName
  );

  // Get all namespace access (policy-based and ConfigMap-based)
  // Similar to useAccesses for environment access
  const { authorizedUsersAndTeams, policyId, policyName, hasPolicyApplied } =
    useNamespaceAccesses(namespaceName);

  const configMap = accessConfigMapQuery.data;
  const updateNamespaceAccessMutation = useUpdateNamespaceAccessesMutation(
    environmentId,
    namespaceName
  );
  const updateConfigMapMutation = useUpdateK8sConfigMapMutation(
    environmentId,
    PortainerNamespaceAccessesConfigMap.namespace
  );

  return (
    <NamespaceAccessDatatable
      tableKey={tableKey}
      dataset={authorizedUsersAndTeams}
      onRemove={handleRemove}
      showWarning={false}
      isUpdateEnabled={!hasPolicyApplied}
      showRoles={false}
      inheritFrom={false}
      policyId={policyId}
      policyName={policyName}
    />
  );

  async function handleRemove(
    selectedAccesses: Array<UserAccessViewModel | TeamAccessViewModel>
  ) {
    if (hasPolicyApplied) {
      // Should not be called when policy is applied, but handle gracefully
      return;
    }

    try {
      const selectedItemsToRemove: NamespaceAccess[] = selectedAccesses.map(
        (access) => ({
          id: access.Id,
          name: access.Name,
          type: access.Type as 'user' | 'team',
          role: access.Role
            ? { id: access.Role.Id, name: access.Role.Name }
            : undefined,
        })
      );

      const configMapPayload = createUnauthorizeAccessConfigMapPayload(
        authorizedUsersAndTeams.map((access) => ({
          id: access.Id,
          name: access.Name,
          type: access.Type as 'user' | 'team',
          role: access.Role
            ? { id: access.Role.Id, name: access.Role.Name }
            : undefined,
        })),
        selectedItemsToRemove,
        namespaceName,
        configMap
      );
      await updateConfigMapMutation.mutateAsync({
        configMap: configMapPayload,
        configMapName: PortainerNamespaceAccessesConfigMap.configMapName,
      });
      const accessesPayload = createAccessesPayload([], selectedItemsToRemove);
      await updateNamespaceAccessMutation.mutateAsync(accessesPayload);
      notifySuccess('Success', 'Namespace access updated');
      router.stateService.reload();
    } catch (error) {
      notifyError('Failed to update namespace access', error as Error);
    }
  }
}
